import { Box, Container, Grid, GridSize, Hidden, IconButton, makeStyles, Theme, Typography } from '@material-ui/core';
import { followUsIconLinks } from 'config/routes';
import { ABOUT_LINKS, GENERAL_LINKS, PRODUCT_LIST_GPM, PRODUCTS_LINKS, SERVICE_LINK_LIST } from 'config/routes/linkLists';
import { useMediaUp } from 'hooks';
import Link from 'next/link';
import { FC, Fragment } from 'react';
import { USSLogoIcon } from 'svgs';
import { IFooterIconList, TLinks } from 'templates/layout/types';

const useStyles = makeStyles((theme: Theme) => ({
  listLabel: {
    marginBottom: theme.spacing(4),
  },
  listItem: {
    fontSize: 18,
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  logoLink: {
    marginRight: theme.spacing(3),
    '&:hover': {
      '& path': {
        fill: 'white',
      },
    },
  },
}));

type TFooterCols = { label: string; links: TLinks[]; lgSize: GridSize };

const footerCols: TFooterCols[] = [
  {
    label: 'Services',
    links: SERVICE_LINK_LIST,
    lgSize: 3,
  },
  {
    label: 'General Links',
    links: GENERAL_LINKS,
    lgSize: 2,
  },
  {
    label: 'GPM',
    links: PRODUCT_LIST_GPM,
    lgSize: 3,
  },
  {
    label: 'Our Work',
    links: PRODUCTS_LINKS,
    lgSize: 2,
  },
];

const Footer: FC = () => {
  const isMobileUp = useMediaUp('xs');
  const currentYear = new Date().getFullYear();
  const isMediumUp = useMediaUp('md');

  return (
    <footer>
      <Box mb={12} />
      <Container fixed>
        <Box pl={isMobileUp ? 0 : 2}>
          <Grid container spacing={6}>
            <Hidden mdUp>
              <Grid lg={3} md={3} xs={12} item>
                <USSLogoIcon />
                <Box mb={10} />
              </Grid>
            </Hidden>
            {footerCols.map(({ lgSize, ...props }, index) => (
              <Grid key={index} lg={lgSize} md={3} sm={6} xs={12} item>
                <LinksList {...props} />
              </Grid>
            ))}
            <Grid lg={2} md={3} sm={6} xs={12} item>
              <LinksList label="About" links={ABOUT_LINKS} />
            </Grid>
          </Grid>
          <Grid
            container
            justifyContent={isMobileUp ? 'flex-start' : 'space-between'}
            alignItems="center"
            style={{ minHeight: '100px' }}
          >
            <Hidden smDown>
              <Grid item lg={6} md={6} xs={12}>
                <Box display="flex" alignItems="center">
                  <USSLogoIcon />
                </Box>
              </Grid>
            </Hidden>
            <Grid item lg={6} md={6} sm={6} xs={12}>
              <Box display="flex" justifyContent={isMediumUp ? 'flex-end' : 'flex-start'} alignItems="center" height="100%">
                <LinksIconList label="Follow Us" links={followUsIconLinks} />
              </Box>
            </Grid>
          </Grid>
          <Box mt={18} mb={5}>
            <Hidden only={['sm', 'xs']} lgUp>
              <Box display="flex" justifyContent="center">
                <USSLogoIcon />
              </Box>
              <Box mb={6} />
            </Hidden>
            <Typography variant="body2" align="center">
              US Software Solutions {currentYear}. All rights reserved
            </Typography>
          </Box>
        </Box>
      </Container>
    </footer>
  );
};

interface ILinkListLocal {
  label: string;
  links: TLinks[];
}

const LinksList: FC<ILinkListLocal> = ({ label, links }) => {
  const classes = useStyles();
  const isMedium = useMediaUp('sm');
  return (
    <Box mb={isMedium ? 7 : 10}>
      <Typography variant="h4" component="h4" className={classes.listLabel}>
        {label}
      </Typography>
      {label === 'Our Work' ? (
        <>
          <Typography style={{ fontWeight: 700, marginBottom: 10 }}>Websites</Typography>
          {links.slice(0, 2).map(({ label, route }, index) => (
            <Fragment key={index}>
              <Link href={route} passHref>
                <Typography component="a" className={classes.listItem}>
                  {label}
                </Typography>
              </Link>
              <Box mb={2} />
            </Fragment>
          ))}
          <Typography style={{ fontWeight: 700, marginBottom: 10 }}>Products</Typography>
          {links.slice(2).map(({ label, route }, index) => (
            <Fragment key={index + 2}>
              <Link href={route} passHref>
                <Typography component="a" className={classes.listItem}>
                  {label}
                </Typography>
              </Link>
              <Box mb={2} />
            </Fragment>
          ))}
        </>
      ) : (
        links.map(({ label, route }, index) => (
          <Fragment key={index}>
            <Link href={route} passHref>
              <Typography component="a" className={classes.listItem}>
                {label}
              </Typography>
            </Link>
            <Box mb={2} />
          </Fragment>
        ))
      )}
    </Box>
  );
};

const LinksIconList: FC<IFooterIconList> = ({ label, links }) => {
  const classes = useStyles();
  const isMedium = useMediaUp('sm');
  return (
    <Box mb={isMedium ? 7 : 10}>
      <Typography variant="h4" component="h4" className={classes.listLabel}>
        {label}
      </Typography>
      {links.map(({ link, logo }, index) => (
        <a href={link} key={index} target="_blank" rel="noopener noreferrer">
          <IconButton color="primary" className={classes.logoLink}>
            {logo}
          </IconButton>
        </a>
      ))}
    </Box>
  );
};

export default Footer;
